import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import cricket from "../../assets/images/menu-4.png";
import fancy from "../../assets/images/fancy.svg";
import tv from "../../assets/images/tv.svg";
import { isAuthenticated } from "../../utils/auth";
import { toast } from "react-toastify";

const formatMatchDate = (openDate) => {
  if (!openDate) return { day: "-", time: "-" };
  const d = new Date(openDate.replace(" ", "T"));
  const day = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return { day, time };
};

const MatchCardRow = ({ match }) => {
  const { day, time } = formatMatchDate(match.open_date);
  const status = String(match.status || "").toUpperCase();
  const isSuspended = status === "SUSPENDED";
  const isClosed = status === "CLOSED";
  const isLive = match.in_play === 1 && !isSuspended && !isClosed;
  const hasTv = !!match.tv_channel;
  const hasBm = match.bookmaker_count > 0;
  const hasFancy = match.enable_fancy === 1;
  const hasCustom = !!match.enableToss;
  const hasPremium = !!match.enablePremium;
  const hasMO = !!match.enableMatchOdds;
  const isLoggedIn = isAuthenticated();
  const navigate = useNavigate();

  const onMatchClick = (url) => {
    if (!isLoggedIn) {
      window.openLoginDialog();
      toast.error("Please login to continue.");
    } else {
      navigate(url);
    }
  };

  const runners = Array.isArray(match.runners) ? match.runners : [];

  const getPriceSize = (runner, key) => {
    if (!runner || !runner.ex) return { price: "-", size: "-" };
    const list =
      key === "back"
        ? runner.ex.availableToBack || []
        : runner.ex.availableToLay || [];
    if (!Array.isArray(list) || list.length === 0) {
      return { price: "-", size: "-" };
    }
    const first = list[0] || {};
    return {
      price:
        first.price != null && first.price !== "" ? String(first.price) : "-",
      size: first.size != null && first.size !== "" ? String(first.size) : "-",
    };
  };

  let cells = [
    { back: { price: "-", size: "-" }, lay: { price: "-", size: "-" } },
    { back: { price: "-", size: "-" }, lay: { price: "-", size: "-" } },
    { back: { price: "-", size: "-" }, lay: { price: "-", size: "-" } },
  ];

  if (!isSuspended && runners.length >= 1) {
    // First team (runners[0]) fills first column (back & lay)
    cells[0] = {
      back: getPriceSize(runners[0], "back"),
      lay: getPriceSize(runners[0], "lay"),
    };
  }

  if (!isSuspended && runners.length >= 2) {
    // Second team (runners[1]) fills last column (back & lay)
    cells[2] = {
      back: getPriceSize(runners[1], "back"),
      lay: getPriceSize(runners[1], "lay"),
    };
  }

  return (
    <div className="bet-table-row">
      <div className="row">
        <div className="col-md-6">
          <div className="game-box">
            <div className="game-left-col">
              <div className="game-name ">
                <div
                  onClick={() =>
                    onMatchClick(`/sports/detail/${match.event_id}`)
                  }
                  style={{ cursor: "pointer" }}
                >
                  <p className="team-name text-left">{match.name}</p>
                  <p className="team-name text-left team-event">
                    ({match.competition_name})
                  </p>
                </div>
              </div>
              <div className="game-date inplay">
                {isLive && <span>Live</span>}
              </div>
              <div className="game-date">
                <p className="mb-0 day text-left">{day}</p>
                <p className="mb-0 day text-left">{time}</p>
              </div>
            </div>
            <div className="game-icons">
              <div className="icon-tv">
                {hasTv && (
                  <Link to="#" onClick={(e) => e.preventDefault()}>
                    <img src={tv} alt="TV" />
                  </Link>
                )}
                {hasFancy && (
                  <Link to="#" onClick={(e) => e.preventDefault()}>
                    <img src={fancy} alt="Fancy" />
                  </Link>
                )}
                {hasPremium && <span className="game-bm bg-success">P</span>}
                {hasBm && <span className="game-bm">BM</span>}
                {hasCustom && <span className="game-custom">T</span>}
                {hasMO && <span className="game-mo">MO</span>}
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6 text-center">
          <div
            className={`row g-0 match-odds-wrapper${
              isSuspended ? " is-suspended" : ""
            }`}
          >
            {isSuspended && <div className="suspended">suspended</div>}
            {cells.map((cell, index) => (
              <div key={index} className="col-md-4 col-4">
                <div className="h-backLay">
                  <div className="back bl-box">
                    <span className="d-block bet-button-price">
                      {cell.back.price} <em>{cell.back.size}</em>
                    </span>
                  </div>
                  <div className="bl-box lay">
                    <span className="d-block bet-button-price">
                      {cell.lay.price} <em>{cell.lay.size}</em>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MatchCard = ({
  sportTitle = "Cricket",
  sportIcon = cricket,
  matches = [],
  instanceId = "",
}) => {
  const [liveChecked, setLiveChecked] = useState(false);
  const [virtualChecked, setVirtualChecked] = useState(false);
  const [upcomingChecked, setUpcomingChecked] = useState(false);
  // const [premiumChecked, setPremiumChecked] = useState(false);

  const filteredMatches = useMemo(() => {
    if (!liveChecked && !virtualChecked && !upcomingChecked) return matches;
    return matches.filter((m) => {
      const inPlayRaw = m?.in_play ?? m?.inPlay ?? m?.inplay ?? false;
      const isLive = inPlayRaw === 1 || inPlayRaw === true;
      const isVirtual =
        m?.catId === "SR VIRTUAL" ||
        Boolean(m?.virtualEvent) ||
        m?.provider === 2;
      const isUpcoming = !isLive;
      if (liveChecked && isLive) return true;
      if (virtualChecked && isVirtual) return true;
      if (upcomingChecked && isUpcoming) return true;
      return false;
    });
  }, [matches, liveChecked, virtualChecked, upcomingChecked]);

  const idPrefix = `matchcard-${sportTitle.replace(/\s+/g, "-").toLowerCase()}${instanceId ? `-${instanceId}` : ""}`;

  return (
    <>
      <div className="bet-table-header">
        <div className="row align-items-center g-0">
          <div className="col-md-6">
            <span className="list-sport-title">
              <img
                className="img-fluid game-icon-img"
                src={sportIcon}
                alt={sportTitle}
              />
              {sportTitle}
            </span>
            <ul className="ks-cboxtags">
              <li>
                <input
                  type="checkbox"
                  id={`${idPrefix}-live`}
                  checked={liveChecked}
                  onChange={(e) => setLiveChecked(e.target.checked)}
                />
                <label htmlFor={`${idPrefix}-live`}>LIVE</label>
              </li>
              <li>
                <input
                  type="checkbox"
                  id={`${idPrefix}-virtual`}
                  checked={virtualChecked}
                  onChange={(e) => setVirtualChecked(e.target.checked)}
                />
                <label htmlFor={`${idPrefix}-virtual`}>VIRTUAL</label>
              </li>
              <li>
                <input
                  type="checkbox"
                  id={`${idPrefix}-upcoming`}
                  checked={upcomingChecked}
                  onChange={(e) => setUpcomingChecked(e.target.checked)}
                />
                <label htmlFor={`${idPrefix}-upcoming`}>UPCOMING</label>
              </li>
            </ul>
          </div>
          <div className="col-md-2 text-center d-none d-md-block">
            <small>1</small>
          </div>
          <div className="col-md-2 text-center d-none d-md-block">
            <small>X</small>
          </div>
          <div className="col-md-2 text-center d-none d-md-block">
            <small>2</small>
          </div>
        </div>
      </div>
      <div className="bet-table-body">
        {filteredMatches.length === 0 ? (
          <div className="bet-table-row">
            <div className="row">
              <div className="col-12 text-center py-4 text-muted">
                No matches
              </div>
            </div>
          </div>
        ) : (
          filteredMatches.map((match) => (
            <MatchCardRow key={match.event_id} match={match} />
          ))
        )}
      </div>
    </>
  );
};

export default MatchCard;
